import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import moment from "moment-timezone";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/select";
import ResourceSelect from "../../../inputs/ResourceSelect";
import type { ResourceItem } from "../../../inputs/ResourceSelect/types";
import { useDateFilters } from "../context/useDateFilters";
import useApi from "../../../../../utils/api";
import {
    renderSettingsResourceLabel,
    getSettingsResourceSearchFiltersWithLookups,
} from "../../../../../utils/helpers/settings";
import {
    getEndDateFromStartDate,
    getStartDateFromEndDate,
} from "../../../../../features/Settings/PayPolicies/PayGroup/Form/helpers";

type PayGroupItem = ResourceItem & {
    frequency: string;
    periodStartDate: string;
    periodEndDate: string;
};

function PayGroup() {
    const { t } = useTranslation();
    const { authGet } = useApi();
    const { payGroupCode, payGroupId, payGroupPeriod, dispatch } = useDateFilters();

    const { data } = useQuery({
        queryKey: ["/pay-group?pagination=false"],
        queryFn: ({ queryKey }: any) => authGet(queryKey[0]),
    });

    const _payGroup: PayGroupItem | undefined = useMemo(() => {
        return data?.result?.find((item: PayGroupItem) => item?.code === payGroupCode);
    }, [data?.result, payGroupCode]);

    const query = useQuery({
        queryKey: [`/pay-group/${_payGroup?.id}/items`],
        queryFn: ({ queryKey }: any) =>
            authGet(queryKey[0], {
                params: {
                    page: 1,
                    perPage: 2,
                    "orderBy[lockTimestamp]": "desc",
                },
            }),
        enabled: !!_payGroup?.id,
    });

    const payGroupCalculation = useCallback(
        (frequency: string, periodStartDate: string, periodEndDate: string, period?: string) => {
            let from: string | undefined, to: string | undefined;
            period = period || payGroupPeriod;
            periodStartDate = moment.parseZone(periodStartDate).toISOString(true);
            periodEndDate = moment.parseZone(periodEndDate).toISOString(true);
            const result = query.data?.result;
            switch (period) {
                case "past": {
                    if (result?.length === 2) {
                        from = moment.parseZone(result[1].periodStartDate).toISOString(true);
                        to = moment.parseZone(result[1].periodEndDate).toISOString(true);
                    } else if (result?.length === 1) {
                        to = moment.parseZone(result[0].periodStartDate).subtract(1, "day").toISOString(true);
                        const start = getStartDateFromEndDate(moment.parseZone(to), frequency);
                        from = start ? moment(start).toISOString(true) : undefined;
                    } else {
                        to = moment.parseZone(periodStartDate).subtract(1, "day").toISOString(true);
                        const start = getStartDateFromEndDate(moment.parseZone(to), frequency);
                        from = start ? moment(start).toISOString(true) : undefined;
                    }
                    break;
                }
                case "current": {
                    if (result?.length > 0) {
                        from = moment.parseZone(result[0].periodStartDate).toISOString(true);
                        to = moment.parseZone(result[0].periodEndDate).toISOString(true);
                    } else {
                        from = periodStartDate;
                        to = periodEndDate;
                    }
                    break;
                }
                case "next": {
                    if (result?.length > 1) {
                        const end = result[0].periodEndDate;
                        from = moment.parseZone(end).add(1, "day").toISOString(true);
                        const nextEnd = getEndDateFromStartDate(from, frequency);
                        to = nextEnd ? moment(nextEnd).toISOString(true) : undefined;
                    } else {
                        from = moment.parseZone(periodEndDate).add(1, "day").toISOString(true);
                        const nextEnd = getEndDateFromStartDate(from, frequency);
                        to = nextEnd ? moment(nextEnd).toISOString(true) : undefined;
                    }
                    break;
                }
                default: {
                    period = "current";
                    if (result?.length === 2) {
                        from = moment.parseZone(result[0].periodStartDate).toISOString(true);
                        to = moment.parseZone(result[0].periodEndDate).toISOString(true);
                    } else if (result?.length === 1) {
                        // no dates until the pay group's items finish loading
                    } else {
                        from = periodStartDate;
                        to = periodEndDate;
                    }
                    break;
                }
            }
            dispatch({ type: "set-pay-group-period", payload: { payGroupPeriod: period } });
            dispatch({
                type: "set-range",
                payload: {
                    from: moment(from).format("YYYY-MM-DD"),
                    to: moment(to).format("YYYY-MM-DD"),
                },
            });
        },
        [dispatch, payGroupPeriod, query.data?.result],
    );

    const onPayGroupChange = useCallback(
        (value?: ResourceItem | ResourceItem[]) => {
            const item = (Array.isArray(value) ? value[0] : value) as PayGroupItem | undefined;
            if (item) {
                const { frequency, periodStartDate, periodEndDate, id, code } = item;
                payGroupCalculation(frequency, periodStartDate, periodEndDate);
                dispatch({ type: "set-pay-group", payload: { payGroupId: id, payGroupCode: code } });
            }
        },
        [dispatch, payGroupCalculation],
    );

    const selectedPayGroup = useMemo(() => {
        if (!payGroupId) return undefined;
        return { id: payGroupId };
    }, [payGroupId]);

    const onPeriodChange = useCallback((value: string) => {
        if (_payGroup) {
            const { frequency, periodStartDate, periodEndDate } = _payGroup;
            payGroupCalculation(frequency, periodStartDate, periodEndDate, value);
        }
        dispatch({ type: "set-pay-group-period", payload: { payGroupPeriod: value } });
    }, [dispatch, _payGroup, payGroupCalculation]);

    return (
        <div className="tw-flex tw-w-full tw-flex-col tw-gap-3 tw-p-5 tw-mb-4">
            <h3 className="tw-uppercase tw-text-xs tw-text-muted-foreground tw-px-2 tw-font-semibold">{t("pay-group")}</h3>
            <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-end">
                <div className="tw-min-w-0 tw-flex-1">
                    <ResourceSelect
                        resourcePath="/pay-group"
                        mode="single"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilter={getSettingsResourceSearchFiltersWithLookups as () => Record<string, unknown>}
                        onChange={onPayGroupChange}
                        value={selectedPayGroup}
                        params={{ pagination: "off" }}
                        className="tw-w-full tw-min-w-0"
                        allowClear
                    />
                </div>

                <div className="tw-w-full tw-shrink-0 sm:tw-w-44">
                    <Select value={payGroupPeriod} onValueChange={onPeriodChange}>
                        <SelectTrigger className="tw-h-9 tw-w-full tw-cursor-pointer">
                            <SelectValue placeholder={t("period")} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem className="tw-cursor-pointer" value="past">
                                {t("past")}
                            </SelectItem>
                            <SelectItem className="tw-cursor-pointer" value="current">
                                {t("current")}
                            </SelectItem>
                            <SelectItem className="tw-cursor-pointer" value="next">
                                {t("next")}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </div>
    );
}

export default PayGroup;
